import React, { useEffect, useContext } from 'react';
import { Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { selectSelectedOfferId } from 'app/store/data/selectedOffer';
import { getOffer } from 'app/store/data/quotationResult';
import { delay } from 'app/shared/helpers/delay';
import { InstallmentsEnum, MessageTypeEnum, LabelEnum } from 'app/shared/enums';
import { HiProDecisionStatus } from 'app/shared/interfaces/HiProDecisionStatus.interface';
import { setItem } from 'app/services/storage.service';
import { HiProPaymentService } from 'app/services/hiProPayment.services';
import {
  ErrorConfig,
  ErrorDecision,
  getMubiInstalmentError,
} from 'app/shared/helpers/getMubiInstalmentError';
import { useNotification } from 'app/shared/hooks/useNotification';
import { MubiInstallmentsContext } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments.context';
import { NewButton } from 'app/components/NewButton';
import { Loader } from 'app/components/Loader';
import { useMubiInstallments } from 'app/components/MubiInstallmentsRadioOption/useMubiInstallments';
import { SafeHtml } from 'app/components/SafeHtml';
import { VerificationProps } from './Verification.interfaces';
import { useStyles } from './Verification.styles';
import { selectIsFormMicroCenterActive } from 'app/store/selectors/selectIsFormMicroCenterActive';

const Verification = (props: VerificationProps) => {
  const classes = useStyles();
  const notify = useNotification();

  const offerId = useSelector(selectSelectedOfferId);
  const offer = useSelector(getOffer(offerId));
  const isFormMicroCenterActive = useSelector(selectIsFormMicroCenterActive);
  const { clearTokenState } = useMubiInstallments(offer);

  const {
    hiProToken,
    setInstallmentOffer,
    closeModal,
    setErrorOnModal,
    setActiveStep,
    mubiInstallmentsDataLayerEvent,
  } = useContext(MubiInstallmentsContext);

  const retryCheckApplicationStatus = async () => {
    await delay(10);
    checkApplicationStatus();
  };

  const checkApplicationStatus = async () => {
    if (!hiProToken) {
      return;
    }
    await HiProPaymentService.getHiProCreditDecision({ token: hiProToken })
      .then(({ data }) => {
        if (data.value === HiProDecisionStatus.Accepted) {
          setInstallmentOffer(data);
          setActiveStep(4);
        } else if (data.value === HiProDecisionStatus.Waiting) {
          retryCheckApplicationStatus();
        } else if (data.value === HiProDecisionStatus.Rejected) {
          setActiveStep(7);
        }
      })
      .catch(({ response }) => {
        const hiProError =
          response?.data &&
          getMubiInstalmentError(
            response.data.errorKey || response.data.message
          );

        if (!hiProError) {
          notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
        } else if (hiProError === ErrorDecision.NegativeDecision) {
          setActiveStep(7);
        } else {
          closeModal();
          setErrorOnModal(hiProError as ErrorConfig);
          clearTokenState();
        }
      })
      .finally(() => {
        setItem(`hiPro_final_decision_${hiProToken}`, { finalDecision: true });
      });
  };

  useEffect(() => {
    checkApplicationStatus();
    mubiInstallmentsDataLayerEvent(
      InstallmentsEnum.EventModalStep,
      InstallmentsEnum.EventStepNameMotionVerification
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={classes.modalContent}>
        <Loader className={classes.loader} />
        <SafeHtml
          html={InstallmentsEnum.VerificationInProgress}
          className={classes.label}
        />
        <Typography className={classes.subLabel}>
          {InstallmentsEnum.PleaseBePatient}
        </Typography>
      </div>
      <NewButton
        className={classes.submitButton}
        fullWidth
        title={InstallmentsEnum.Next}
        variant="contained"
        color="primary"
        disabled={true}
        rounded={isFormMicroCenterActive}
      />
    </>
  );
};

export default Verification;
